import React, { useEffect } from 'react';

import { IconButton, Stack, Typography, RefreshIcon } from '@gogame/components';
import { withForm, getIn, SidePageV2 } from '@gogame/form';
import { NumberValueFormatter } from '@gogame/intl-ts';

import {
  EditGranularityProvider,
  EDIT_GRANULARITY_ALL,
} from 'scenes/CustomAudiences/EditGranularityContext';
import PartnerAudiencesFieldset from 'scenes/CustomAudiences/PartnerAudiences/components/PartnerAudienceForm/PartnerAudiencesFieldset';

import { useEstimateAudience } from '../../data/mutations';
import ActivityFieldSet from './ActivityFieldSet';
import AudienceTypesFormField from './AudienceTypesFormField';
import * as AUDIENCE_TYPES_FIELDSET from './AudienceTypesFormField/constants';
import DepositorsFieldSet from './DepositorsFieldSet';
import EngagementEventsFormField from './EngagementEventsFormField';
import GeneralFieldSet from './GeneralFieldSet';
import PopulationFieldSet from './PopulationFieldSet';
import { NETWORKS, PLATFORMS } from './PopulationFieldSet/constants';
import {
  DEPOSITORS,
  AUDIENCE_TYPES,
  PARTNER_AUDIENCES,
  getAudienceFromValues,
} from './constants';
import { AudienceFormValidationSchema } from './schemas';

// TODO: fix render form before loading data, problem with isControlled

const AudienceForm = withForm({
  validationSchema: AudienceFormValidationSchema,
  enableReinitialize: true,
})(({
  title,
  subTitle,
  loading,
  setFieldValue,
  onClose,
  values,
  onExited,
  open,
  validateForm,
  isShowPartnerAudiences,
  entityId,
  editGranularity = EDIT_GRANULARITY_ALL,
}) => {
  const [estimateAudience, { data, isLoading }] = useEstimateAudience();

  let audienceTypes = getIn(values, AUDIENCE_TYPES);
  let depositors = getIn(values, DEPOSITORS);

  const handleEstimate = async () => {
    let errors = await validateForm();
    let isValid = Object.keys(errors).length === 0;
    if (isValid) {
      const audience = getAudienceFromValues(values);
      await estimateAudience(audience);
    }
  };

  useEffect(() => {
    if (!Array.isArray(audienceTypes)) return;

    const isAll =
      audienceTypes.length ===
      Object.keys(AUDIENCE_TYPES_FIELDSET.NAMES).length;

    if (isAll) {
      if (depositors) {
        setFieldValue(DEPOSITORS, undefined);
      }
    } else {
      const hasRegular = audienceTypes.includes(
        AUDIENCE_TYPES_FIELDSET.REGULAR
      );
      if (hasRegular && !depositors) {
        setFieldValue(DEPOSITORS, {});
      }
      if (!hasRegular && depositors) {
        setFieldValue(DEPOSITORS, undefined);
      }
    }
  }, [audienceTypes]);

  return (
    <SidePageV2
      onClose={onClose}
      onExited={onExited}
      open={open}
      loading={loading}
      size={'lg'}
    >
      <SidePageV2.Title title={title} subTitle={subTitle} entityId={entityId} />
      <SidePageV2.NewBody>
        <EditGranularityProvider editGranularity={editGranularity}>
          <GeneralFieldSet />
          <PopulationFieldSet />
          <AudienceTypesFormField />
          <ActivityFieldSet />
          {depositors ? <DepositorsFieldSet /> : null}
          <EngagementEventsFormField />
          {isShowPartnerAudiences ? (
            <PartnerAudiencesFieldset
              name={PARTNER_AUDIENCES}
              depends={[NETWORKS, PLATFORMS]}
            />
          ) : null}
        </EditGranularityProvider>
      </SidePageV2.NewBody>
      <SidePageV2.Actions justifyContent={'space-between'}>
        <Stack direction={'row'}>
          <Stack direction={'row'} spacing={1}>
            <Stack>
              <Typography color={'textSecondary'} variant={'body2'}>
                GG audience size
              </Typography>
              {isLoading ? (
                <Typography>Loading...</Typography>
              ) : (
                <Typography>
                  <NumberValueFormatter.Decimal value={data?.quantity} />
                </Typography>
              )}
            </Stack>
            <Stack>
              <Typography color={'textSecondary'} variant={'body2'}>
                ATT excluded
              </Typography>
              {isLoading ? (
                <Typography>Loading...</Typography>
              ) : (
                <Typography>
                  <NumberValueFormatter.Decimal
                    value={data?.attExcludedQuantity}
                  />
                </Typography>
              )}
            </Stack>
          </Stack>
          <IconButton
            icon={<RefreshIcon />}
            onClick={handleEstimate}
            disabled={isLoading}
            tooltip={'Estimate'}
          >
            Estimate
          </IconButton>
        </Stack>
        <Stack direction={'row'} spacing={2}>
          <SidePageV2.CloseButton title={'Close'} />
          <SidePageV2.SubmitButton />
        </Stack>
      </SidePageV2.Actions>
    </SidePageV2>
  );
});

export default AudienceForm;
